import React from 'react';
import {
  Check,
  Inbox,
  Search,
  Package,
  PackageCheck,
  Wrench,
  ShieldCheck,
  CircleCheck,
  AlertTriangle,
} from 'lucide-react';
import {
  SAV_TIMELINE_STEPS,
  formatSavDate,
  savTimelineView,
  stepTimestamp,
} from './posV2SavUtils';

const STEP_ICONS = [Inbox, Search, Package, PackageCheck, Wrench, ShieldCheck, CircleCheck];

const PosV2SavTimeline = ({ device }) => {
  const { status, activeIndex, allDone, progressPct, isFailure } = savTimelineView(device);
  const currentStep = SAV_TIMELINE_STEPS[activeIndex];

  return (
    <div
      className="rounded-xl border border-white/10 bg-gradient-to-b from-black/40 to-black/20 p-4 text-gray-200 overflow-hidden"
      data-testid="repair-sav-timeline"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Suivi de réparation
          </h3>
          {status && (
            <p className="text-sm font-semibold text-white mt-1">
              Statut actuel · <span className="text-amber-300">{status}</span>
            </p>
          )}
          {currentStep && !allDone && !isFailure && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              Étape en cours : {currentStep.label}
            </p>
          )}
          {allDone && (
            <p className="text-[11px] text-emerald-400 mt-0.5">Parcours terminé — appareil livré ou prêt</p>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-gray-500">Avancement</div>
          <div className="text-lg font-bold tabular-nums text-red-400">{progressPct}%</div>
        </div>
      </div>

      <div className="relative h-1.5 rounded-full bg-white/10 mb-5 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-red-700 via-amber-500 to-emerald-500 transition-all duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {isFailure && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-200">
          <AlertTriangle size={14} className="shrink-0" />
          Réparation arrêtée — statut : {status}
        </div>
      )}

      <div className="overflow-x-auto pb-1 -mx-1 px-1 sav-timeline-scroll">
        <div className="flex min-w-[640px] gap-0">
          {SAV_TIMELINE_STEPS.map((step, idx) => {
            const Icon = STEP_ICONS[idx] || Wrench;
            const done = allDone || idx < activeIndex;
            const current = !allDone && idx === activeIndex && !isFailure;
            const ts = formatSavDate(stepTimestamp(device, idx));
            const isLast = idx === SAV_TIMELINE_STEPS.length - 1;

            return (
              <div key={step.id} className="flex flex-1 min-w-0 items-start">
                <div className="flex flex-col items-center flex-1 min-w-[72px]">
                  <div className="relative">
                    {current && (
                      <span
                        className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping"
                        aria-hidden
                      />
                    )}
                    <div
                      className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-300 ${
                        done
                          ? 'bg-red-600 border-red-500 text-white scale-100'
                          : current
                            ? 'bg-amber-500 border-amber-300 text-white shadow-lg shadow-amber-500/30 scale-110'
                            : 'bg-black/60 border-white/15 text-gray-500'
                      }`}
                    >
                      {done ? <Check size={18} strokeWidth={3} /> : <Icon size={16} />}
                    </div>
                  </div>
                  <div className="mt-2 text-center min-w-0 w-full px-0.5">
                    <div
                      className={`text-[11px] font-semibold leading-tight ${
                        current ? 'text-amber-300' : done ? 'text-gray-200' : 'text-gray-500'
                      }`}
                    >
                      <span className="hidden sm:inline">{step.label}</span>
                      <span className="sm:hidden">{step.shortLabel}</span>
                    </div>
                    {ts && (
                      <div className="text-[9px] text-gray-500 mt-0.5 leading-tight">{ts}</div>
                    )}
                  </div>
                </div>
                {!isLast && (
                  <div
                    className={`h-0.5 mt-5 flex-1 min-w-[8px] max-w-[24px] rounded transition-colors duration-500 ${
                      done ? 'bg-red-600' : 'bg-white/10'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PosV2SavTimeline;
